// src/components/Admin/Leads/LeadForm.jsx
import React, { useEffect, useMemo, useState, Fragment } from "react";
import {
  Box,
  Button,
  Grid,
  MenuItem,
  TextField,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAuth } from "../../../hook";
import { getStages, getSources, createLead, updateLead } from "../../../api/crm";

/**
 * LeadForm con estructura mejorada + pestañas (MUI Tabs)
 * - Tab 1: Datos básicos (title, contact_email, contact_phone, estimated_value)
 * - Tab 2: Datos avanzados (company_name, stage, source, status, notes)
 * - Se refactoriza campos repetidos con <FormTextField/>
 * - Se mantiene Formik + Yup y el flujo de create/update
 */
export function LeadForm({ lead, onClose, onSaved, defaultStageId }) {
  const isEdit = !!lead?.id;
  const { auth } = useAuth();

  const [stages, setStages] = useState([]);
  const [sources, setSources] = useState([]);
  const [loadingLists, setLoadingLists] = useState(true);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const [st, so] = await Promise.all([
          getStages(auth.token),
          getSources(auth.token),
        ]);
        setStages(Array.isArray(st) ? st : []);
        setSources(Array.isArray(so) ? so : []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingLists(false);
      }
    })();
  }, [auth?.token]);

  const validation = useMemo(() => Yup.object(validationSchema()), []);

  const formik = useFormik({
    initialValues: initialValues(lead, defaultStageId),
    validationSchema: validation,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = mapToApi(values);
        if (isEdit) {
          await updateLead(lead.id, payload, auth.token);
        } else {
          await createLead(payload, auth.token);
        }
        onSaved?.();
        onClose?.();
      } catch (err) {
        console.error(err);
        alert("No se pudo guardar el lead. Revisa los datos.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleChangeTab = (_e, value) => setTab(value);

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      onSubmit={formik.handleSubmit}
      sx={{ minWidth: 380 }}
    >
      {/* Header de pestañas */}
      <Tabs value={tab} onChange={handleChangeTab} aria-label="Pestañas Lead">
        <Tab label="Datos básicos" />
        <Tab label="Datos avanzados" />
      </Tabs>
      <Divider sx={{ mb: 2 }} />

      {/* TAB 0: Datos básicos */}
      <TabPanel value={tab} index={0}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormTextField
              formik={formik}
              name="title"
              label="Nombre del Cliente"
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormTextField
              formik={formik}
              name="company_name"
              label="Empresa"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormTextField
              formik={formik}
              name="contact_email"
              label="Email de contacto"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormTextField
              formik={formik}
              name="contact_phone"
              label="Teléfono de contacto"
            />
          </Grid>
          

          <Grid item xs={12} md={6}>
            <FormTextField
              formik={formik}
              name="notes"
              label="Requerimiento"
              multiline
              rows={3}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormTextField
              formik={formik}
              name="estimated_value"
              label="Valor estimado"
              type="number"
              inputProps={{ step: "0.01" }}
            />
          </Grid>

        </Grid>
      </TabPanel>

      {/* TAB 1: Datos avanzados */}
      <TabPanel value={tab} index={1}>
        <Grid container spacing={2}>
          
          <Grid item xs={12} md={6}>
            <FormSelect
              formik={formik}
              name="stage"
              label="Etapa"
              disabled={loadingLists}
              options={stages.map((s) => ({ value: s.id, label: s.name }))}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormSelect
              formik={formik}
              name="source"
              label="Fuente"
              disabled={loadingLists}
              options={sources.map((s) => ({ value: s.id, label: s.name }))}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormSelect
              formik={formik}
              name="status"
              label="Estado"
              options={STATUS_OPTIONS}
            />
          </Grid>
          
        </Grid>
      </TabPanel>

      {/* acciones */}
      <Box display="flex" gap={1} mt={3}>
        <Button
          type="submit"
          variant="contained"
          color="info"
          disabled={formik.isSubmitting}
        >
          {formik.isSubmitting
            ? "Guardando..."
            : isEdit
            ? "Guardar cambios"
            : "Crear lead"}
        </Button>
        <Button
          type="button"
          variant="outlined"
          color="inherit"
          onClick={onClose}
          disabled={formik.isSubmitting}
        >
          Cancelar
        </Button>
      </Box>
    </Box>
  );
}

/** -------------------- helpers & subcomponentes -------------------- */

const STATUS_OPTIONS = [

  { value: "open", label: "open" },
  { value: "won", label: "won" },
  { value: "lost", label: "lost" },
];

function initialValues(lead, defaultStageId) {
  const stageFromLead = lead?.stage?.id ?? lead?.stage ?? "";
  return {
    title: lead?.title || "",
    company_name: lead?.company_name || "",
    contact_email: lead?.contact_email || "",
    contact_phone: lead?.contact_phone || "",
    estimated_value: lead?.estimated_value ?? "",
    stage: stageFromLead || (defaultStageId ?? ""),
    source: lead?.source?.id ?? lead?.source ?? "",
    status: lead?.status || "",
    notes: lead?.notes || "",
  };
}

function validationSchema() {
  return {
    title: Yup.string().required("Requerido"),
    company_name: Yup.string(),
    contact_email: Yup.string()
      .email("Email inválido")
      .nullable()
      .transform((v) => (v === "" ? null : v)),
    contact_phone: Yup.string(),
    estimated_value: Yup.number()
      .typeError("Debe ser numérico")
      .min(0, ">= 0")
      .nullable()
      .transform((v) => (v === "" ? null : Number(v))),
    stage: Yup.mixed(),
    source: Yup.mixed(),
    status: Yup.string(),
    notes: Yup.string(),
  };
}

function mapToApi(values) {
  return {
    title: values.title,
    company_name: values.company_name || null,
    contact_email:
      values.contact_email && values.contact_email.trim() !== ""
        ? values.contact_email.trim()
        : null,
    contact_phone:
      values.contact_phone && values.contact_phone.trim() !== ""
        ? values.contact_phone.trim()
        : null,
    estimated_value:
      values.estimated_value === "" || values.estimated_value === null
        ? null
        : Number(values.estimated_value),
    stage_id: values.stage ? Number(values.stage) : null,
    source_id: values.source ? Number(values.source) : null,
    status: values.status || "open",
    notes: values.notes || "",
  };
}

/**
 * TabPanel simple para MUI Tabs
 */
function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index} aria-labelledby={`tab-${index}`}>
      {value === index ? <Box sx={{ pt: 1 }}>{children}</Box> : null}
    </div>
  );
}

/**
 * Campo de texto genérico conectado a Formik
 */
function FormTextField({ formik, name, label, ...props }) {
  const error = Boolean(formik.errors[name]);
  const helperText = formik.errors[name] || "";
  return (
    <TextField
      fullWidth
      label={label}
      name={name}
      value={formik.values[name]}
      onChange={formik.handleChange}
      error={error}
      helperText={helperText}
      {...props}
    />
  );
}

/**
 * Select genérico conectado a Formik
 */
function FormSelect({ formik, name, label, options = [], ...props }) {
  const error = Boolean(formik.errors[name]);
  const helperText = formik.errors[name] || "";
  return (
    <TextField
      select
      fullWidth
      label={label}
      name={name}
      value={formik.values[name]}
      onChange={formik.handleChange}
      error={error}
      helperText={helperText}
      {...props}
    >
      <MenuItem value="">-- Seleccionar --</MenuItem>
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </TextField>
  );
}
